import { Component, computed, effect, inject, input, InputSignal, signal, WritableSignal, } from "@angular/core";
import { DatePipe, KeyValuePipe, NgClass, NgOptimizedImage, DecimalPipe } from "@angular/common";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatSortModule, Sort } from "@angular/material/sort";
import { ActivatedRoute, Params, RouterLink } from "@angular/router";
import {
    CaseIncidentResponse,
    Incident,
    IncidentCaseQueryParams,
    IncidentSource,
    IncidentStage,
    IncidentStatus,
    PaginatorSetting,
    TableSetting,
    TableSettingRequest,
} from "../../api/models/api.model";
import { MatDialog } from "@angular/material/dialog";
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap, } from "rxjs/operators";
import { interval, retry, take } from "rxjs";
import { ApiService } from "../../api/services/api.service";
import { Pagination } from "../../api/models/pagination.model";
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { CopyButtonComponent } from "../../partials/components/copy-button/copy-button.component";
import { SearchService } from "../../partials/services/search.service";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AddInvoiceComponent } from "../../partials/components/dialogs/add-invoice/add-invoice.component";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { CropWithDotsCenterPipe } from "../../partials/pipes/crop-with-dots-center.pipe";
import { MatTooltip } from "@angular/material/tooltip";
import { MatCheckbox, MatCheckboxChange } from "@angular/material/checkbox";
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray, } from "@angular/cdk/drag-drop";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { Store } from "@ngrx/store";
import { ProfileSelectors, ProfileStoreState, Role } from "../../core/store/profile";
import { TelegramUserLinkComponent } from "../../partials/components/telegram-user-link/telegram-user-link.component";
// MatCard removed — source filter cards replaced by stage funnel bar
import {
    MatDatepickerToggle,
    MatDateRangeInput,
    MatDateRangePicker,
    MatEndDate,
    MatStartDate
} from "@angular/material/datepicker";
import { MatError, MatFormField, MatLabel, MatSuffix } from "@angular/material/form-field";
import { dateRangeValidator } from "../../partials/validators/validators";
import moment, { Moment } from "moment/moment";
import { MatSelectModule } from "@angular/material/select";
// environment import removed — no longer needed for default source filter
import { NewRowsTrackerService } from "../../partials/services/new-rows-tracker.service";
import { DocumentTitleService } from "../../partials/services/document-title.service";
import { TranslateModule } from "@ngx-translate/core";

// Настройки таблицы которые сохраняются в API
export interface RequestTableSettings {
    allDisplayedColumns: string[];
    filteredDisplayedColumns: string[];
}

// Имя таблицы необходимо для сохранения настроек в API
export const requestTableName = "RequestTable";

@Component({
    selector: "app-requests",
    standalone: true,
    imports: [
    MatPaginator,
    MatProgressSpinner,
    MatTableModule,
    MatSortModule,
    NgClass,
    NgOptimizedImage,
    RouterLink,
    FormsModule,
    CopyButtonComponent,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    DatePipe,
    CropWithDotsCenterPipe,
    MatTooltip,
    MatCheckbox,
    CdkDropList,
    CdkDrag,
    CdkScrollable,
    CdkDragHandle,
    TelegramUserLinkComponent,
    MatButton,
    MatSortModule,
    MatDateRangeInput,
    MatDateRangePicker,
    MatDatepickerToggle,
    MatEndDate,
    MatError,
    MatFormField,
    MatLabel,
    MatStartDate,
    MatSuffix,
    ReactiveFormsModule,
    KeyValuePipe,
    DecimalPipe,
    TranslateModule
],
    templateUrl: "./requests.component.html",
    styleUrl: "./requests.component.scss",
})
export default class RequestsComponent {
    initCaseIncidentResponse: InputSignal<Pagination<CaseIncidentResponse>> =
        input.required<Pagination<CaseIncidentResponse>>();

    adminRole = input.required<Role | undefined>();

    private readonly _matDialog: MatDialog = inject(MatDialog);

    private readonly _apiService: ApiService = inject(ApiService);

    private readonly _searchService: SearchService = inject(SearchService);


    private readonly _tracker: NewRowsTrackerService = inject(NewRowsTrackerService);
    readonly newIds = this._tracker.newIds('requests');

    private readonly _docTitle: DocumentTitleService = inject(DocumentTitleService);

    private readonly _profileStore: Store<{ profile: ProfileStoreState }> =
        inject<Store<{ profile: ProfileStoreState }>>(Store);

    user = toSignal(
        this._profileStore.select(ProfileSelectors.selectProfileInformation),
    );

    readonly dataSource: MatTableDataSource<CaseIncidentResponse> =
        new MatTableDataSource<CaseIncidentResponse>([]);

    readonly loadedTableSettings: InputSignal<
        TableSetting<RequestTableSettings> | undefined
    > = input<TableSetting<RequestTableSettings> | undefined>();

    allDisplayedColumns: string[] = [
        "userId",
        "id",
        "source",
        "telegramUsername",
        "phone",
        "email",
        "country",
        "victimType",
        "scamTypeId",
        "initiatedAt",
        "submittedAt",
        "updatedAt",
        "kyc",
        "caseAmount",
        "leadStatus",
        "paymentDue",
        "totalCost",
        "numberOfInvoices",
        "actions",
    ];

    filteredDisplayedColumns: string[] = [];

    /** PII + admin-only columns hidden from compliance operators. */
    private static readonly COMPLIANCE_HIDDEN_COLUMNS = [
        'telegramUsername',
        'telegramUserId',
        'phone',
        'email',
        'country',
        'numberOfInvoices',
        'paymentDue',
        'totalCost',
        'actions',
    ];

    get isCompliance(): boolean {
        return this.adminRole() === 'COMPLIANCE';
    }

    get menuDisplayedColumns(): string[] {
        const filteredColumns: string[] = [];
        if (this.isCompliance) {
            filteredColumns.push(...RequestsComponent.COMPLIANCE_HIDDEN_COLUMNS);
        }
        return this.allDisplayedColumns.filter(
            (column) => !filteredColumns.includes(column),
        );
    }

    get displayedColumns() {
        const filteredColumns = [...this.filteredDisplayedColumns];
        if (this.isCompliance) {
            filteredColumns.push(...RequestsComponent.COMPLIANCE_HIDDEN_COLUMNS);
        }
        return this.allDisplayedColumns.filter(
            (column) => !filteredColumns.includes(column),
        );
    }

    paginator: PaginatorSetting = {
        pageSize: 25,
        pageIndex: 0,
        length: 0,
    };

    private _sort: Sort = { active: 'initiatedAt', direction: 'desc' };
    private readonly _findBy: WritableSignal<string> = signal("");

    readonly stageFilter = signal<IncidentStage | null>(null);
    readonly filterSource = signal<IncidentSource | null>(null);

    private readonly _rejectedSortKeys = new Set<string>();

    get _queryParams() {
        const { pageSize, pageIndex } = this.paginator;
        const { active: sortBy, direction } = this._sort;
        const findBy = this._findBy();
        const stage = this.stageFilter();
        const source = this.filterSource();
        const queryParams: IncidentCaseQueryParams = {
            size: pageSize,
            page: pageIndex,
        };
        if (findBy) {
            queryParams.findBy = findBy;
        }
        if (stage) {
            queryParams.stage = stage;
        }
        if (source) {
            queryParams.source = source;
        }
        if (sortBy && direction !== '' && !this._rejectedSortKeys.has(sortBy)) {
            queryParams.sortBy = sortBy;
            queryParams.sortOrder = direction === 'asc' ? 1 : -1;
        }
        return queryParams;
    }

    readonly loading: WritableSignal<boolean> = signal(false);

    getCaseRoute(row: any): string[] {
        if (row.source === 'STAR_COMPLIANCE') {
            return ['/case-star-compliance', row.id];
        }
        return ['/case', row.id];
    }

    onRowClick(row: CaseIncidentResponse): void {
        this._tracker.markOpened('requests', row.id);
    }

    private readonly _route: ActivatedRoute = inject(ActivatedRoute);

    private readonly _fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);

    readonly rangePickerCSVForm = this._fb.group({
        from: this._fb.control<Moment | null>(null),
        to: this._fb.control<Moment | null>(null),
    }, { validators: dateRangeValidator });

    /**
     * Latest date the CSV range picker will offer. The export covers requests
     * that already exist, so a future date can only ever widen the range to
     * nothing — the picker used to accept one and hand the endpoint a `to` past
     * now (SMC-117).
     */
    readonly maxCSVDate = moment();

    constructor() {
        this._docTitle.bind(this._tracker.count('requests'));

        effect(
            () => {
                const { content, totalElements, number, size } =
                    this.initCaseIncidentResponse();
                this.dataSource.data = content;

                this.paginator.pageSize = size;
                this.paginator.pageIndex = number;
                this.paginator.length = totalElements;
            },
            { allowSignalWrites: true },
        );

        effect(
            () => {
                const loadedTableSettings = this.loadedTableSettings();
                if (loadedTableSettings) {
                    const {
                        data: { allDisplayedColumns, filteredDisplayedColumns },
                    } = loadedTableSettings;

                    let compair = null;
                    if (this.allDisplayedColumns.length !== allDisplayedColumns.length) {
                        compair = true;
                    }
                    this.allDisplayedColumns.forEach((item: string) => {
                        if (!allDisplayedColumns.includes(item)) {
                            compair = true;
                        }
                    });
                    if (compair) {
                        this.setColumn();
                    } else {
                        this.allDisplayedColumns = allDisplayedColumns;
                    }
                    this.filteredDisplayedColumns = filteredDisplayedColumns;
                }
            },
            { allowSignalWrites: true },
        );

        effect(() => {
            const _stage = this.stageFilter();
            const _source = this.filterSource();
            this.paginator.pageIndex = 0;
            this._loadCaseIncidentResponse();
        }, { allowSignalWrites: true });

        // When the layout's background poll detects a new incident id, refresh
        // the table immediately instead of waiting for this page's own 30s tick.
        let lastNewIdsCount: number | null = null;
        effect(() => {
            const count = this.newIds().size;
            if (lastNewIdsCount !== null && count > lastNewIdsCount) {
                this._loadCaseIncidentResponse();
            }
            lastNewIdsCount = count;
        }, { allowSignalWrites: true });

        this._searchService.searchControl.valueChanges
            .pipe(debounceTime(200), distinctUntilChanged(), takeUntilDestroyed())
            .subscribe({
                next: (term) => {
                    this._findBy.set(term);
                    this.paginator.pageIndex = 0;
                    this._loadCaseIncidentResponse();
                },
            });

        this._route.queryParams.pipe(takeUntilDestroyed()).subscribe({
            next: (queryParams: Params) => {
                const findBy = queryParams["findBy"];
                if (findBy) {
                    this._searchService.searchControl.patchValue(findBy);
                }
                this._loadCaseIncidentResponse();
            },
        });

        interval(30_000)
            .pipe(startWith(0), takeUntilDestroyed())
            .subscribe(() => this._loadCaseIncidentResponse());
    }

    private setColumn(): void {
        this._saveTableSettings();
    }

    addInvoice(incident: Incident): void {
        this._matDialog
            .open(AddInvoiceComponent, {
                data: { incident },
                autoFocus: true,
            })
            .afterClosed()
            .pipe(
                filter(Boolean),
                switchMap(() => this._apiService.getIncidentCases()),
                take(1),
            )
            .subscribe({
                next: ({ content }) => {
                    this.dataSource.data = content;
                },
            });
    }


    handlePageEvent(e: PageEvent) {
        this.paginator.pageSize = e.pageSize;
        this.paginator.pageIndex = e.pageIndex;
        this._loadCaseIncidentResponse();
    }

    cancelCase(caseIncidentResponse: CaseIncidentResponse): void {
        const { id } = caseIncidentResponse;
        this._apiService
            .putIncidentStatus(id, { incidentStatus: "ADMIN_CANCELED" })
            .pipe(retry(3), take(1))
            .subscribe({
                next: () => {
                    caseIncidentResponse.incidentStatus = "ADMIN_CANCELED";
                },
            });
    }

    toSuccess(caseIncidentResponse: CaseIncidentResponse): void {
        const { id } = caseIncidentResponse;
        this._apiService
            .putIncidentStatus(id, { incidentStatus: "SUCCESS" })
            .pipe(retry(3), take(1))
            .subscribe({
                next: () => {
                    caseIncidentResponse.incidentStatus = "SUCCESS";
                },
            });
    }

    toDone(caseIncidentResponse: CaseIncidentResponse): void {
        const { id } = caseIncidentResponse;
        this._apiService
            .putIncidentStatus(id, { incidentStatus: "DONE" })
            .pipe(retry(3), take(1))
            .subscribe({
                next: () => {
                    caseIncidentResponse.incidentStatus = "DONE";
                },
            });
    }

    private _loadCaseIncidentResponse(): void {
        this.loading.set(true);
        this._apiService
            .getIncidentCases(this._queryParams)
            .pipe(retry(3), take(1))
            .subscribe({
                next: ({ content, totalElements, number, size }) => {
                    this.dataSource.data = content;

                    this.paginator.pageSize = size;
                    this.paginator.pageIndex = number;
                    this.paginator.length = totalElements;
                    this.loading.set(false);
                },
                error: (err) => {
                    this.loading.set(false);
                    if (this._isInvalidSortError(err)) {
                        const rejectedKey = this._sort.active;
                        if (rejectedKey && !this._rejectedSortKeys.has(rejectedKey)) {
                            this._rejectedSortKeys.add(rejectedKey);
                            console.warn(`BE rejected sortBy=${ rejectedKey }; retrying without sort`);
                            this._loadCaseIncidentResponse();
                        }
                    }
                },
            });
    }

    private _isInvalidSortError(err: unknown): boolean {
        const e = err as { status?: number; error?: { message?: string } };
        if (e?.status !== 400) return false;
        return (e.error?.message ?? '').toLowerCase().includes('invalid sort field');
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(
            this.allDisplayedColumns,
            event.previousIndex,
            event.currentIndex,
        );
        this._saveTableSettings();
    }

    handlerChangeDisplayColumnCheckbox(
        { checked }: MatCheckboxChange,
        displayedColumn: string,
    ): void {
        if (checked) {
            this.filteredDisplayedColumns = this.filteredDisplayedColumns.filter(
                (c) => c !== displayedColumn,
            );
        } else {
            this.filteredDisplayedColumns.push(displayedColumn);
        }
        this._saveTableSettings();
    }

    private _saveTableSettings(): void {
        const user = this.user();
        if (!user) return;
        const { uuid } = user;
        const body: TableSettingRequest = {
            data: {
                allDisplayedColumns: this.allDisplayedColumns,
                filteredDisplayedColumns: this.filteredDisplayedColumns,
            },
            tableName: requestTableName,
            userId: uuid,
        };
        this._apiService
            .createOrUpdateTableSetting<RequestTableSettings>(body)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    console.log(`${ requestTableName } settings saved!`);
                },
            });
    }

    handleSort(sort: Sort) {
        this._sort = sort;
        this._loadCaseIncidentResponse();
    }

    downloadCSV(): void {
        const { from, to } = this.rangePickerCSVForm.getRawValue();

        const queryParams: Partial<{ from: number, to: number }> = {};

        if (from && to) {
            queryParams.from = moment(from).startOf('day').valueOf();
            queryParams.to = moment(to).endOf('day').valueOf();
        }

        this._apiService.getExportedCases(queryParams).pipe(take(1)).subscribe({
            next: (file: Blob) => {
                if (typeof file !== "boolean") {
                    const downloadLink = document.createElement("a");
                    downloadLink.href = window.URL.createObjectURL(file);
                    downloadLink.setAttribute("download", ('Cases'));
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    return false;
                }
                return true;
            }
        });
    }

    asRecord(value: unknown): Record<string, unknown> {
        return value as Record<string, unknown>;
    }
}
